import { useId } from 'react';

import { FileUploadDropzone } from './components/Dropzone';
import { FileCard } from './components/FileCard';

import type { FileWithProgress } from './types';

/** Props do componente FileUpload */
export interface FileUploadProps {
  /** Lista de arquivos com progresso de upload */
  files: FileWithProgress[];
  /** Callback para adicionar novos arquivos */
  addFiles: (files: File[]) => void;
  /** Callback para remover um arquivo pelo nome */
  removeFile: (fileName: string) => void;
  /** Permite seleção de múltiplos arquivos */
  hasMultipleFiles?: boolean;
  /** Exibe estado de erro na dropzone */
  hasError?: boolean;
  /** Texto auxiliar exibido abaixo da dropzone */
  helper?: string;
  /** Tipos de arquivo aceitos (valor do atributo HTML `accept`, ex: ".pdf,.png") */
  accept?: string;
  /** Bloqueia a escolha de arquivo — inclusive o arrastar-e-soltar. */
  disabled?: boolean;
}

/**
 * Componente de upload de arquivos com dropzone e lista de arquivos enviados.
 *
 * @example
 * ```tsx
 * <FileUpload
 *   files={files}
 *   addFiles={addFiles}
 *   removeFile={removeFile}
 * />
 * ```
 */
export function FileUpload({
  files,
  addFiles,
  removeFile,
  hasMultipleFiles = false,
  hasError = false,
  helper = undefined,
  accept = undefined,
  disabled = false,
}: FileUploadProps) {
  const id = useId();

  return (
    <div className="flex flex-col gap-micro">
      {/* Dropzone */}
      <FileUploadDropzone
        addFiles={addFiles}
        hasMultipleFiles={hasMultipleFiles}
        hasError={hasError}
        disabled={disabled}
        {...(helper !== undefined ? { helper } : {})}
        {...(accept !== undefined ? { accept } : {})}
      />

      {/* Uploaded Files */}
      {files.map(file => (
        <FileCard
          key={`${id}-${file.file.name}`}
          fileName={file.file.name}
          {...(file.progress !== undefined ? { progress: file.progress } : {})}
          removeFile={() => removeFile(file.file.name)}
        />
      ))}
    </div>
  );
}
